import { Region, SettingKeyType } from '@crew/enums/app'
import dayjs from '@crew/modules'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting } from '@crew/states'
import classNames from 'classnames'
import { CrewGuideToolTip } from 'components/devextreme/crewGuideToolTip/crewGuideToolTip'
import { CrewNavigateToPaidPlanDialog } from 'components/elements/crewNavigateToPaidPlanDialog/crewNavigateToPaidPlanDialog'
import { useModal } from 'components/layouts/modal/useModal'
import { usePaidFeatureStatus } from 'hooks/usePaidFeatureStatus'
import { memo, useCallback } from 'react'
import { useAppSelector } from 'states/hooks'
import MaterialSymbolsCrown from '~icons/material-symbols/crown'

// キャンペーンツールチップ専用のCSSクラス
// クラスの定義はindex.css内
const tooltipPopoverClass = { class: 'campaign-tooltip' } as const

export const CampaignUpgradeButton = memo(() => {
  const { t } = useTranslation()

  // 有償プランへの誘導ダイアログ
  const [
    isNavigateToPaidPlanDialogOpen,
    openNavigateToPaidPlanDialog,
    closeNavigateToPaidPlanDialog,
  ] = useModal()

  // ユーザー設定からデフォルトのユーザープロファイル地域を取得
  const defaultUserProfileRegion = useUserSetting(
    SettingKeyType.UserProfileRegion,
    Region.Japan.value
  )

  // 有償機能のトライアル期限を取得
  const paidPlanTrialExpirationDatetime = useAppSelector(
    (state) => state.app.paidPlanTrialExpirationDatetime
  )

  // 有償機能が利用可能かどうかを判定する
  const paidFeatureStatus = usePaidFeatureStatus()

  // ボタンのバリエーションを判定
  const buttonVariant = paidFeatureStatus.isTrialPeriod
    ? 'onCampaign'
    : 'upgrade'

  // ボタンのスタイル
  const buttonStyle =
    buttonVariant === 'onCampaign'
      ? 'bg-crew-orange-500 text-crew-white' // キャンペーン中（オレンジ）
      : 'bg-crew-gray-700 text-crew-white' // アップグレード（グレー）

  // ボタンクリック時のハンドラ
  const handleButtonClick = useCallback(() => {
    // 有償プランを利用中の場合は何もしない
    if (
      paidFeatureStatus.canUsePaidFeature &&
      !paidFeatureStatus.isTrialPeriod
    ) {
      return
    }
    // フリープランまたは試用期間終了時はダイアログを開く
    openNavigateToPaidPlanDialog()
  }, [
    paidFeatureStatus.canUsePaidFeature,
    paidFeatureStatus.isTrialPeriod,
    openNavigateToPaidPlanDialog,
  ])

  // キャンペーンツールチップのレンダー関数
  const renderCampaignTooltip = useCallback(() => {
    const formattedExpirationDatetime = dayjs(paidPlanTrialExpirationDatetime)
      .tz(String(defaultUserProfileRegion))
      .format('M/D H:mm') // 月/日 時:分 の形式

    return (
      <CrewGuideToolTip
        target="#campaign-upgrade-button"
        position="bottom"
        // キャンペーンツールチップ専用のCSSクラスで上書き
        wrapperAttr={tooltipPopoverClass}
      >
        <div className="px-3">
          {/* キャンペーン期間 */}
          <div className="flex items-center justify-center gap-1 text-sm">
            <MaterialSymbolsCrown
              className="text-crew-yellow-3-light dark:text-crew-yellow-3-dark"
              width={16}
              height={16}
            />
            <span className="text-crew-white">
              {t('label.campaignUpgrade.campaignToolTip.markFeatureTrial', {
                expirationDatetime: formattedExpirationDatetime,
              })}
            </span>
          </div>

          {/* 特典内容 */}
          <div className="flex items-center justify-center gap-1 text-lg">
            <span className="text-crew-white">
              {t('label.campaignUpgrade.campaignToolTip.trialPeriod')}
            </span>
            <span className="text-crew-yellow-3-light dark:text-crew-yellow-3-dark">
              {t('label.campaignUpgrade.campaignToolTip.oneMonthFree')}
            </span>
          </div>
        </div>
      </CrewGuideToolTip>
    )
  }, [defaultUserProfileRegion, paidPlanTrialExpirationDatetime, t])

  // 有償プランを既に利用中の場合は何も表示しない
  if (paidFeatureStatus.canUsePaidFeature && !paidFeatureStatus.isTrialPeriod) {
    return null
  }

  return (
    <>
      <div id="campaign-upgrade-button">
        <span
          className={classNames(
            // 基本スタイル
            'px-3 py-1 text-sm rounded-md',
            // 折り返し防止
            'whitespace-nowrap flex-shrink-0',
            // カーソル
            'cursor-pointer select-none',
            // キャンペーン / アップグレード のスタイル
            buttonStyle
          )}
          onClick={handleButtonClick}
        >
          {t(`label.campaignUpgrade.${buttonVariant}`)}
        </span>
        {/* キャンペーン中の場合、tooltipを表示 */}
        {buttonVariant === 'onCampaign' && renderCampaignTooltip()}
      </div>
      {/* 有償プランへの誘導ダイアログ */}
      <CrewNavigateToPaidPlanDialog
        isOpen={isNavigateToPaidPlanDialogOpen}
        onClose={closeNavigateToPaidPlanDialog}
      />
    </>
  )
})
